/**
 * MinaRoMina - Product Price Style Fix
 *
 * Neutraliza la inyección dinámica de margin-top negativo que Shopify aplica
 * sobre elementos de precio (.mm-product-info__price-current) sin romper
 * el sistema de precios dinámicos.
 *
 * Problema: Shopify inyecta inline styles con !important en modo preview/theme editor
 * Solución: Observer + forced style override después de cada mutación del DOM
 */

(function() {
  'use strict';

  const CONFIG = {
    // Selectores de elementos que Shopify modifica
    priceCurrentSelector: '.mm-product-info__price-current',
    priceContainerSelector: '[data-price-container]',

    // Estilos a forzar (solo margin-top, no tocamos el resto)
    forcedStyles: {
      'margin-top': '0px'
    },

    // Eventos de Shopify a escuchar
    shopifyEvents: [
      'shopify:section:load',
      'shopify:section:select',
      'shopify:section:reorder',
      'shopify:block:select',
      'mm:variant:change'
    ],

    // Debounce para evitar ejecuciones excesivas
    debounceDelay: 50
  };

  class PriceStyleFixer {
    constructor() {
      this.observers = [];
      this.debounceTimer = null;
      this.isFixing = false;
      this.init();
    }

    init() {
      // Ejecutar fix inicial
      this.fixPriceStyles();

      // Escuchar eventos estándar
      this.attachStandardListeners();

      // Escuchar eventos de Shopify
      this.attachShopifyListeners();

      // Observar mutaciones del DOM
      this.observePriceElements();

      // Log de inicialización
      console.log('[PriceStyleFixer] Initialized and monitoring price elements');
    }

    /**
     * Aplica los estilos forzados a los elementos de precio
     */
    fixPriceStyles() {
      if (this.isFixing) return;
      this.isFixing = true;

      const priceElements = document.querySelectorAll(CONFIG.priceCurrentSelector);

      priceElements.forEach(element => {
        // Usar setProperty con 'important' flag para sobrescribir inline !important
        Object.entries(CONFIG.forcedStyles).forEach(([property, value]) => {
          element.style.setProperty(property, value, 'important');
        });
      });

      this.isFixing = false;
    }

    /**
     * Versión con debounce para evitar ejecuciones masivas
     */
    fixPriceStylesDebounced() {
      clearTimeout(this.debounceTimer);
      this.debounceTimer = setTimeout(() => {
        this.fixPriceStyles();
      }, CONFIG.debounceDelay);
    }

    /**
     * Adjunta listeners a eventos estándar del DOM
     */
    attachStandardListeners() {
      // DOMContentLoaded (si aún no ha cargado)
      if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', () => {
          this.fixPriceStyles();
        });
      }

      // Load completo
      window.addEventListener('load', () => {
        this.fixPriceStyles();
      });

      // Resize (Shopify a veces recalcula estilos responsive)
      let resizeTimer;
      window.addEventListener('resize', () => {
        clearTimeout(resizeTimer);
        resizeTimer = setTimeout(() => {
          this.fixPriceStyles();
        }, 300);
      });
    }

    /**
     * Adjunta listeners a eventos específicos de Shopify
     */
    attachShopifyListeners() {
      CONFIG.shopifyEvents.forEach(eventName => {
        document.addEventListener(eventName, () => {
          // Delay breve para que Shopify termine de aplicar sus estilos primero
          setTimeout(() => {
            this.fixPriceStyles();
          }, 100);
        });
      });
    }

    /**
     * Observa cambios en los elementos de precio usando MutationObserver
     */
    observePriceElements() {
      // Observer global para detectar cuando se añaden nuevos elementos de precio
      const globalObserver = new MutationObserver((mutations) => {
        let shouldFix = false;

        mutations.forEach(mutation => {
          // Detectar si se añadieron nodos con clase de precio
          if (mutation.addedNodes.length > 0) {
            mutation.addedNodes.forEach(node => {
              if (node.nodeType === Node.ELEMENT_NODE) {
                if (node.matches && node.matches(CONFIG.priceCurrentSelector)) {
                  shouldFix = true;
                } else if (node.querySelector && node.querySelector(CONFIG.priceCurrentSelector)) {
                  shouldFix = true;
                }
              }
            });
          }

          // Detectar cambios en atributos de elementos de precio
          if (mutation.type === 'attributes' &&
              mutation.attributeName === 'style' &&
              mutation.target.matches &&
              mutation.target.matches(CONFIG.priceCurrentSelector)) {
            shouldFix = true;
          }
        });

        if (shouldFix) {
          this.fixPriceStylesDebounced();
        }
      });

      // Observar todo el documento
      globalObserver.observe(document.body, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['style']
      });

      this.observers.push(globalObserver);

      // Observer específico para elementos de precio existentes
      const priceElements = document.querySelectorAll(CONFIG.priceCurrentSelector);
      priceElements.forEach(element => {
        const elementObserver = new MutationObserver((mutations) => {
          mutations.forEach(mutation => {
            if (mutation.type === 'attributes' && mutation.attributeName === 'style') {
              // Verificar si Shopify cambió el margin-top
              const currentMarginTop = element.style.getPropertyValue('margin-top');
              if (currentMarginTop && currentMarginTop !== '0px') {
                this.fixPriceStylesDebounced();
              }
            }
          });
        });

        elementObserver.observe(element, {
          attributes: true,
          attributeFilter: ['style']
        });

        this.observers.push(elementObserver);
      });
    }

    /**
     * Destruye todos los observers (útil para cleanup)
     */
    destroy() {
      this.observers.forEach(observer => observer.disconnect());
      this.observers = [];
      clearTimeout(this.debounceTimer);
      console.log('[PriceStyleFixer] Destroyed');
    }
  }

  // Inicializar el fixer
  const priceFixer = new PriceStyleFixer();

  // Exponer globalmente para debugging
  window.MinaRominaPriceFixer = priceFixer;

  // Ejecutar fix adicional después de delays comunes de Shopify
  setTimeout(() => priceFixer.fixPriceStyles(), 500);
  setTimeout(() => priceFixer.fixPriceStyles(), 1000);
  setTimeout(() => priceFixer.fixPriceStyles(), 2000);

})();
